import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { Input } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Switch } from "../../../../components/common/switch";
import { Heading, Text } from "../../../../components/common/typography";

interface BulkApproveDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onApprove: (marginPercentage: number, roundUp: boolean) => Promise<void>;
}

export function BulkApproveDialog({ open, setOpen, onApprove }: BulkApproveDialogProps) {
  const [marginValue, setMarginValue] = useState("10");
  const [roundUp, setRoundUp] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMarginValue("10");
      setRoundUp(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleClose = () => { if (!isApproving) setOpen(false); };

  const handleSubmit = async () => {
    if (isApproving) return;
    const marginNum = parseFloat(marginValue);
    if (isNaN(marginNum) || marginNum < 0) {
      setError("Please enter a valid number (0 or greater)");
      return;
    }
    setIsApproving(true);
    setError(null);
    try {
      await onApprove(marginNum, roundUp);
      setOpen(false);
    } catch {
      setError("Failed to approve product requests. Please try again.");
    } finally {
      setIsApproving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative w-full max-w-lg mx-4 rounded-2xl bg-bgWhite shadow-2xl">
        <div className="flex flex-col gap-6 p-8">
          <div className="flex items-start justify-between gap-4">
            <Heading variant="h3" tone="dark">Bulk Approve Product Requests</Heading>
            <Button variant="ghost" size="sm" className="!px-1 shrink-0 -mt-1" onClick={handleClose}>
              <IconX size={20} />
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <Text variant="caps" tone="dark">Margin Percentage</Text>
            <Input
              name="marginPercentage"
              type="number"
              value={marginValue}
              onChange={(e) => { setMarginValue(e.target.value); setError(null); }}
              placeholder="10%"
              min={0}
              error={error ?? undefined}
            />
            <Text variant="bodySm" tone="muted">
              This margin will be applied to seller pricing for all pending product requests
            </Text>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={roundUp} onChange={setRoundUp} aria-label="Round up prices to nearest x9.99" />
            <Text variant="bodySm" tone="dark">Round up to x9.99</Text>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={handleSubmit} disabled={isApproving} className="flex-1">
              {isApproving ? "Approving…" : "Approve"}
            </Button>
            <Button variant="secondary" onClick={handleClose} disabled={isApproving} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
